import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Input, Button, Alert } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useUserActivity } from '../../../hooks/useUserActivity';
import {
  formatActivityEntry,
  type UserActivityEntry,
} from '../../../services/userManagementService';
import { NotesCard } from '../../../components/user-detail';
import { formatVnDateTimeEn } from '../../../utils/vnDateTime';

const ROUNDED = { borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };

interface ActivityLogTabProps {
  userId: string;
  noteValue: string;
  onNoteChange: (v: string) => void;
  onSaveNote?: () => void;
}

export const ActivityLogTab: React.FC<ActivityLogTabProps> = ({
  userId,
  noteValue,
  onNoteChange,
  onSaveNote,
}) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const limit = 20;

  const {
    data,
    isLoading: loading,
    isError: error,
    refetch,
  } = useUserActivity(userId, { page, limit, search });

  const activities = data?.data ?? [];
  const pagination = data?.pagination;

  if (error) {
    return (
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}
      >
        <Alert
          type="error"
          message="Không thể tải nhật ký hoạt động."
          showIcon
          action={
            <Button size="small" onClick={() => refetch()}>
              Thử lại
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}>
      <div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Input
            placeholder="Search activity..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ flex: 1, maxWidth: 400, borderRadius: 8 }}
          />
        </div>

        <Card title="Timeline" style={ROUNDED} loading={loading}>
          <div style={{ position: 'relative', paddingLeft: 32 }}>
            <div
              style={{
                position: 'absolute',
                left: 11,
                top: 0,
                bottom: 0,
                width: 2,
                background: '#e5e7eb',
              }}
            />
            {activities.length === 0 && !loading ? (
              <div style={{ color: '#6b7280', padding: '24px 0' }}>
                No activity recorded
              </div>
            ) : (
              activities.map((item: UserActivityEntry, idx: number) => {
                const { title, description } = formatActivityEntry(item);
                const orderId =
                  item.metadata?.orderId ??
                  ((item.metadata?.orderNumber as string)?.replace(/\D/g, '') ||
                    '');
                return (
                  <div
                    key={`${item.action}-${item.createdAt}-${idx}`}
                    style={{
                      position: 'relative',
                      paddingBottom: 24,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: -32,
                        top: 4,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: '#2563eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 12,
                      }}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 16,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            color: '#111827',
                            marginBottom: 4,
                          }}
                        >
                          {title}
                        </div>
                        <div style={{ fontSize: 14, color: '#4b5563' }}>
                          {orderId ? (
                            <>
                              {description
                                ?.split(/(Order #ORD-\d{6}-\d+)/)
                                .map((part, j) =>
                                  /Order #ORD-\d{6}-\d+/.test(part) ? (
                                    <Link
                                      key={j}
                                      to={`/orders/${orderId}`}
                                      style={{ color: '#2563eb' }}
                                    >
                                      {part}
                                    </Link>
                                  ) : (
                                    part
                                  )
                                )}
                            </>
                          ) : (
                            description
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            marginTop: 8,
                            flexWrap: 'wrap',
                          }}
                        >
                          <div
                            style={{
                              fontSize: 12,
                              color: '#6b7280',
                            }}
                          >
                            {formatVnDateTimeEn(item.createdAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {pagination && pagination.total > limit && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                style={{ marginRight: 8 }}
              >
                Previous
              </Button>
              <span style={{ margin: '0 16px' }}>
                Page {page} of {pagination.pages}
              </span>
              <Button
                disabled={page >= pagination.pages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </Card>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Card title="Device & Location" style={ROUNDED}>
          <div style={{ color: '#6b7280', padding: '16px 0' }}>
            No device or location data available
          </div>
        </Card>
        <NotesCard
          value={noteValue}
          onChange={onNoteChange}
          onSave={onSaveNote}
          showSaveButton={true}
        />
      </div>
    </div>
  );
};
